import React from 'react';
import { Crosshair, AlertTriangle, CheckCircle2, FileSearch } from 'lucide-react';

const KeywordConflictsPanel = ({ project }) => {
  const conflicts = project?.keyword_analysis?.cannibalization || null;

  if (!conflicts) {
    return (
      <div className="bg-black/30 border border-gray-850 p-8 rounded-2xl text-center animate-in fade-in duration-300">
        <Crosshair size={32} className="text-gray-700 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          No cannibalization analysis available. Re-run an audit to detect pages competing for the same target keywords.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Conflict Clusters</span>
          <span className={`text-xl font-mono font-black ${conflicts.total_clusters > 0 ? 'text-amber-400' : 'text-green-400'}`}>
            {conflicts.total_clusters}
          </span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">High-Risk Conflicts</span>
          <span className={`text-xl font-mono font-black ${conflicts.high_risk_clusters > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {conflicts.high_risk_clusters}
          </span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Pages Affected</span>
          <span className="text-xl font-mono font-black text-white">{conflicts.affected_pages}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Status</span>
          <span className={`text-sm font-bold ${conflicts.is_clean ? 'text-green-400' : 'text-amber-400'}`}>
            {conflicts.is_clean ? 'Clean' : 'Conflicts Found'}
          </span>
        </div>
      </div>

      {conflicts.is_clean && (
        <div className="bg-green-950/20 border border-green-500/25 rounded-2xl p-5 flex items-center gap-3">
          <CheckCircle2 size={18} className="text-green-400 shrink-0" />
          <p className="text-xs text-green-200">
            No pages are competing for the same target keyword — every sampled URL has distinct targeting.
          </p>
        </div>
      )}

      {/* Clusters */}
      {conflicts.clusters?.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <FileSearch size={15} className="text-blue-400" />
            Competing Page Clusters ({conflicts.clusters.length} shown)
          </h4>
          <div className="flex flex-col gap-3">
            {conflicts.clusters.map((cluster, idx) => (
              <div key={idx} className="bg-black/30 border border-gray-850 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border tracking-wider shrink-0 ${
                      cluster.risk === 'High' ? 'text-red-400 bg-red-950/40 border-red-500/30' : 'text-amber-400 bg-amber-950/40 border-amber-500/30'
                    }`}>
                      {cluster.risk} Risk
                    </span>
                    <span className="font-mono text-xs font-bold text-amber-200 truncate">"{cluster.keyword}"</span>
                  </div>
                  <span className="text-[10px] text-gray-500 font-mono shrink-0">{cluster.competing_pages} pages competing</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {cluster.pages.map((page, pIdx) => (
                    <div key={pIdx} className="bg-gray-950/80 border border-gray-850 rounded-xl p-3 flex flex-col gap-1">
                      <a href={page.url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-blue-400 hover:underline truncate">
                        {page.url}
                      </a>
                      <span className="text-[10px] text-gray-400 truncate">{page.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-amber-950/20 border border-amber-500/25 rounded-2xl p-4 text-xs text-amber-200 leading-relaxed">
            <span className="font-bold">Why it matters: </span>
            When multiple pages target the same keyword, Google must pick one winner — diluting rankings, authority, and
            CTR across all of them. Consolidate the weakest pages (301) or differentiate targeting with unique modifiers.
          </div>
        </div>
      )}

      {/* Recommendations */}
      {conflicts.recommendations?.length > 0 && !conflicts.is_clean && (
        <div className="flex flex-col gap-2">
          {conflicts.recommendations.map((rec, i) => (
            <div key={i} className="flex items-start gap-2.5 text-xs text-gray-300 bg-black/25 border border-gray-850 rounded-xl p-3">
              <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
              <span className="leading-relaxed">{rec}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KeywordConflictsPanel;
